import React, { useEffect } from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import {
  isSurpriseMeActive,
  useMysteryBoxSelectionsContext,
} from '~/shared/utils/mysteryBoxSelections';
import { mobile } from '~/techstyle-shared/react-components';

import MysteryBoxSectionDivider from '../MysteryBoxSectionDivider';

const Wrapper = styled.div`
  padding: 20px 0 16px;
  border-bottom: 1px solid
    ${({ theme }) => theme.colors.mysteryBoxDividerBorder};

  ${mobile`
    padding-top: 20px;
  `}
`;

const QuestionRow = styled.div`
  margin-bottom: 16px;
`;

const QuestionLabel = styled.div`
  color: ${({ theme }) => theme.colors.mysteryBoxLabel};
  font-size: 14px;
  font-weight: 400;
  margin-bottom: 12px;

  ${mobile`
    padding: 0 16px;
  `}
`;

const QuestionSublabel = styled.div`
  color: ${({ theme }) => theme.colors.mysteryBoxSubLabel};
  font-size: 12px;
  margin-bottom: 12px;

  ${mobile`
    padding: 0 16px;
  `}
`;

const ChipList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  list-style: none;
  margin: 0;
  padding: 0;

  ${mobile`
    flex-wrap: nowrap;
    overflow-x: auto;
    padding: 0 16px;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
  `}
`;

const ChipInput = styled.input.attrs({ type: 'checkbox' })`
  opacity: 0;
  position: absolute;
`;

const ChipItem = styled.li`
  flex-shrink: 0;
  position: relative;
`;

const ChipLabel = styled.label`
  align-items: center;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  font-size: 14px;
  height: 40px;
  padding: 0 12px;
  white-space: nowrap;

  ${ChipInput}:checked + & {
    border: 2px solid ${({ theme }) => theme.colors.mysteryBoxSelectionOutline};
  }
`;

const SwatchCircle = styled.span`
  background-image: url(${({ $desktopSrc }) => $desktopSrc});
  background-size: cover;
  border-radius: 50%;
  flex-shrink: 0;
  height: 18px;
  margin-right: 6px;
  width: 18px;

  ${mobile`
    background-image: url(${({ $mobileSrc }) => $mobileSrc});
  `}
`;

export default function MysteryBoxStyleSection({ config }) {
  const { styleSelections, toggleStyle, setStyleQuestions } =
    useMysteryBoxSelectionsContext();

  useEffect(() => {
    setStyleQuestions(config.styleQuestionList);
  }, [config.styleQuestionList, setStyleQuestions]);

  return (
    <Wrapper>
      {config.styleSectionTitle && (
        <MysteryBoxSectionDivider>
          {config.styleSectionTitle}
        </MysteryBoxSectionDivider>
      )}
      {config.styleQuestionList.map((question) => {
        const questionId = String(question.mysteryBoxPreferenceTypeId);
        const visibleAnswers = question.answers.filter((a) => !a.hidden);
        const surpriseActive = isSurpriseMeActive(styleSelections, questionId);

        return (
          <QuestionRow key={questionId}>
            <QuestionLabel>{question.label}</QuestionLabel>
            {question.subLabel && (
              <QuestionSublabel>{question.subLabel}</QuestionSublabel>
            )}
            <ChipList>
              {visibleAnswers.map((answer, index) => {
                const isSurpriseMe = index === 0;
                const isChecked = isSurpriseMe
                  ? surpriseActive
                  : (styleSelections[questionId] ?? []).includes(answer.label);
                const inputId = `mystery-style-${questionId}-${answer.label.replace(
                  /\W+/g,
                  '-'
                )}`;
                const hasImage = answer.desktopImage || answer.mobileImage;

                return (
                  <ChipItem key={answer.label}>
                    <ChipInput
                      id={inputId}
                      checked={isChecked}
                      onChange={() =>
                        toggleStyle(questionId, answer.label, isSurpriseMe)
                      }
                    />
                    <ChipLabel htmlFor={inputId}>
                      {hasImage && (
                        <SwatchCircle
                          $desktopSrc={
                            answer.desktopImage || answer.mobileImage
                          }
                          $mobileSrc={answer.mobileImage || answer.desktopImage}
                        />
                      )}
                      {answer.label}
                    </ChipLabel>
                  </ChipItem>
                );
              })}
            </ChipList>
          </QuestionRow>
        );
      })}
    </Wrapper>
  );
}

MysteryBoxStyleSection.propTypes = {
  config: PropTypes.shape({
    styleSectionTitle: PropTypes.string,
    styleQuestionList: PropTypes.arrayOf(
      PropTypes.shape({
        mysteryBoxPreferenceTypeId: PropTypes.number.isRequired,
        label: PropTypes.string.isRequired,
        sublabel: PropTypes.string,
        answers: PropTypes.arrayOf(
          PropTypes.shape({
            label: PropTypes.string.isRequired,
            desktopImage: PropTypes.string,
            mobileImage: PropTypes.string,
            hidden: PropTypes.bool,
          })
        ).isRequired,
      })
    ).isRequired,
  }).isRequired,
};
